<template>
  <section class="flight-direction-editor">
    <header class="flight-direction-editor__header">
      <div>
        <div class="flight-direction-editor__eyebrow">
          <span class="flight-direction-editor__eyebrow-label">{{ directionLabel }} review</span>
          <app-status-badge design="new" :label="reviewStatusLabel" :tone="reviewStatusTone" compact />
        </div>
        <h3>Review final values</h3>
        <p>{{ reviewedResultSummary }}</p>
      </div>
      <div class="flight-direction-editor__header-actions">
        <button
          v-if="selectedSegment"
          type="button"
          class="flight-direction-editor__preview"
          @click="$emit('preview', selectedSegment.id)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h9L20 9.5v9A1.5 1.5 0 0 1 18.5 20h-13A1.5 1.5 0 0 1 4 18.5z" />
            <path d="M14 4v6h6M8 14h8M8 17h5" />
          </svg>
          <span>View ticket</span>
        </button>
        <button
          v-if="!readonly"
          type="button"
          class="flight-direction-editor__save"
          :disabled="saving || !canSave"
          @click="save"
        >
          {{ saving ? 'Saving…' : 'Save review draft' }}
        </button>
      </div>
    </header>

    <fieldset class="flight-direction-editor__controls" :class="{ 'flight-direction-editor__controls--readonly': readonly }" :disabled="readonly">
    <div class="flight-direction-editor__segment">
      <app-form-group design="new" label="Recognized segment" required>
        <app-select
          v-model="form.segment_id"
          design="new"
          :options="segments"
          :reduce="segment => segment.id"
          :display-field="segmentLabel"
          label="flight_number"
          :clearable="false"
          placeholder="Select a segment"
          @input="onSegmentChange"
        />
      </app-form-group>
      <div v-if="selectedSegment" class="flight-direction-editor__route">
        <span>{{ selectedSegment.departure_airport_iata || 'Not set' }}</span>
        <strong>→</strong>
        <span>{{ selectedSegment.arrival_airport_iata || 'Not set' }}</span>
        <small :class="{ 'flight-direction-editor__manual': !isCandidate }">
          {{ isCandidate ? 'AI recommendation' : 'Manual segment' }}
        </small>
      </div>
    </div>

    <template v-if="selectedSegment">
      <direction-comparison-panel
        :rows="fieldRows"
        :conflict-rows="conflictRows"
        :matching-rows="matchingRows"
        :change-count="reviewedChangeCount"
        :airports="airports"
        :show-matching="showMatchingFields"
        :editing-fields="editingMatchingFields"
        @update:show-matching="showMatchingFields = $event"
        @use-value="useFieldValue"
        @set-value="setReviewedValue"
        @toggle-edit="toggleMatchingFieldEdit"
      />
    </template>
    </fieldset>

    <p v-if="unmappedIssues.length" class="flight-direction-editor__issue-summary">
      {{ unmappedIssues.length }} additional review
      {{ unmappedIssues.length === 1 ? 'issue applies' : 'issues apply' }} to this direction.
    </p>
  </section>
</template>

<script>
import { format, isValid, parse, parseISO } from 'date-fns'
import DirectionComparisonPanel from './DirectionComparisonPanel'

const FIELDS = [
  { key: 'flight_number', label: 'Flight number', formKey: 'flight_number', aliases: ['flight number', 'flight_number'] },
  { key: 'date', label: 'Date', formKey: 'date', aliases: ['date'] },
  { key: 'time', label: 'Time', formKey: 'time', aliases: ['time'] },
  { key: 'airport_iata', label: 'Airport (IATA)', formKey: 'airport_iata', aliases: ['airport', 'iata', 'location'] },
  { key: 'airline_name', label: 'Airline', formKey: 'airline_name', aliases: ['airline', 'company'] },
  { key: 'flight_domestic', label: 'Domestic flight', formKey: 'is_domestic', aliases: ['domestic'] },
]

export default {
  name: 'FlightTicketDirectionEditor',
  components: { DirectionComparisonPanel },
  props: {
    direction: { type: String, required: true },
    segments: { type: Array, required: true },
    selection: { type: Object, default: null },
    comparison: { type: Object, default: null },
    airports: { type: Array, default: () => [] },
    issues: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      isDirty: false,
      showMatchingFields: false,
      editingMatchingFields: [],
      form: {
        segment_id: null,
        flight_number: null,
        date: null,
        time: null,
        airport_iata: null,
        airline_name: null,
        is_domestic: false,
      },
    }
  },
  computed: {
    directionLabel() {
      return this.direction === 'arrival' ? 'Arrival' : 'Departure'
    },
    selectedSegment() {
      return this.segments.find(segment => segment.id === this.form.segment_id) || null
    },
    isCandidate() {
      if (!this.selectedSegment) return false
      const key = this.direction === 'arrival' ? 'is_arrival_candidate' : 'is_departure_candidate'
      return Boolean(this.selectedSegment[key])
    },
    fieldRows() {
      return FIELDS.map(field => {
        const reviewedValue = this.reviewedValue(field.key)
        const currentValue = this.currentValue(field.key)
        const aiValue = this.aiValue(field.key)
        const issue = this.fieldIssue(field.key)
        const bothMissing = this.isMissing(reviewedValue) && this.isMissing(currentValue)
        let state = 'different'
        if (bothMissing) state = 'empty'
        else if (this.comparisonValuesEqual(field.key, reviewedValue, currentValue)) state = 'matching'
        return {
          ...field,
          state,
          issue,
          needsReview: this.fieldHasConflict(field.key) || Boolean(issue),
          reviewedValue,
          currentValue,
          aiValue,
          matchesClient: this.comparisonValuesEqual(field.key, reviewedValue, currentValue),
          matchesAi: this.comparisonValuesEqual(field.key, reviewedValue, aiValue),
          reviewedSourceLabel: this.reviewedSourceLabel(field.key),
          clientDisplay: this.displayValue(field.key, currentValue),
          aiDisplay: this.displayValue(field.key, aiValue),
          reviewedDisplay: this.displayValue(field.key, reviewedValue),
        }
      })
    },
    conflictRows() {
      return this.fieldRows.filter(row => row.needsReview)
    },
    matchingRows() {
      return this.fieldRows.filter(row => !row.needsReview)
    },
    reviewedChangeCount() {
      return this.fieldRows.filter(row => row.state === 'different').length
    },
    reviewedResultSummary() {
      if (!this.comparison) return 'Compare client data with AI extraction and confirm the result.'
      if (!this.reviewedChangeCount) return 'The reviewed result currently matches client data.'
      return `${this.reviewedChangeCount} client ${this.reviewedChangeCount === 1 ? 'change is' : 'changes are'} staged in this draft.`
    },
    reviewStatusLabel() {
      if (this.isDirty) return 'Unsaved changes'
      if (this.selection?.source === 'user') return 'Draft saved'
      return 'Needs review'
    },
    reviewStatusTone() {
      if (this.isDirty || this.selection?.source !== 'user') return 'amber'
      return 'green'
    },
    canSave() {
      return Boolean(
        this.form.segment_id
        && (this.isDirty || this.selection?.source !== 'user'),
      )
    },
    mappedIssueIds() {
      return FIELDS.reduce((result, field) => {
        const issue = this.fieldIssue(field.key)
        if (issue) result.push(issue.id)
        return result
      }, [])
    },
    unmappedIssues() {
      return this.issues.filter(issue => !this.mappedIssueIds.includes(issue.id))
    },
  },
  watch: {
    selection: {
      immediate: true,
      deep: true,
      handler() {
        this.initialize()
      },
    },
    segments() {
      this.initialize()
    },
  },
  methods: {
    initialize() {
      const candidateKey = this.direction === 'arrival' ? 'is_arrival_candidate' : 'is_departure_candidate'
      const segment = this.selection?.segment
        || this.segments.find(item => item[candidateKey])
        || this.segments[0]
      this.form.segment_id = segment?.id || null
      this.populate(segment, this.selection?.overrides || {})
      this.isDirty = false
      this.showMatchingFields = false
      this.editingMatchingFields = []
      this.$emit('dirty-change', { direction: this.direction, dirty: false })
    },
    onSegmentChange() {
      this.populate(this.selectedSegment, {})
      this.markDirty()
    },
    populate(segment, overrides) {
      if (!segment) return
      const prefix = this.direction
      this.form.flight_number = overrides.flight_number ?? segment.flight_number ?? null
      this.form.date = overrides[`${prefix}_date`] ?? segment[`${prefix}_date`] ?? null
      this.form.time = this.normalizeTime(
        overrides[`${prefix}_time`] ?? segment[`${prefix}_time`] ?? null,
      )
      this.form.airport_iata = (
        overrides[`${prefix}_airport_iata`] ?? segment[`${prefix}_airport_iata`] ?? null
      )
      this.form.airline_name = overrides.airline_name ?? segment.airline_name ?? null
      this.form.is_domestic = overrides.is_domestic ?? segment.is_domestic ?? false
    },
    segmentLabel(segment) {
      const flight = segment.flight_number || 'Flight number unknown'
      const route = `${segment.departure_airport_iata || 'Not set'} → ${segment.arrival_airport_iata || 'Not set'}`
      const date = segment[`${this.direction}_date`]
        || segment[`${this.direction === 'arrival' ? 'departure' : 'arrival'}_date`]
      return `${flight} · ${route} · ${date ? this.formatDate(date) : 'Date unknown'}`
    },
    fieldDefinition(key) {
      return FIELDS.find(field => field.key === key)
    },
    reviewedValue(key) {
      const field = this.fieldDefinition(key)
      return field ? this.form[field.formKey] : null
    },
    currentValue(key) {
      if (key === 'flight_domestic') {
        return this.comparison?.current?.flight_domestic
          ?? this.comparison?.current?.is_domestic
      }
      return this.comparison?.current?.[key]
    },
    aiValue(key) {
      if (!this.selectedSegment) return null
      if (key === 'date') return this.selectedSegment[`${this.direction}_date`]
      if (key === 'time') return this.selectedSegment[`${this.direction}_time`]
      if (key === 'airport_iata') return this.selectedSegment[`${this.direction}_airport_iata`]
      if (key === 'flight_domestic') return this.selectedSegment.is_domestic
      return this.selectedSegment[key]
    },
    fieldHasConflict(key) {
      const clientValue = this.currentValue(key)
      const aiValue = this.aiValue(key)
      if (this.isMissing(clientValue) && this.isMissing(aiValue)) return false
      return !this.comparisonValuesEqual(key, clientValue, aiValue)
    },
    reviewedMatches(key, source) {
      const sourceValue = source === 'client' ? this.currentValue(key) : this.aiValue(key)
      return this.comparisonValuesEqual(key, this.reviewedValue(key), sourceValue)
    },
    reviewedSourceLabel(key) {
      const matchesClient = this.reviewedMatches(key, 'client')
      const matchesAi = this.reviewedMatches(key, 'ai')
      if (matchesClient && matchesAi) return 'Client and AI match'
      if (matchesClient) return 'Using client value'
      if (matchesAi) return 'Using AI value'
      return 'Manual value'
    },
    useFieldValue(key, source) {
      let value = source === 'client' ? this.currentValue(key) : this.aiValue(key)
      if (key === 'time') value = this.normalizeTime(value)
      if (key === 'flight_domestic') value = Boolean(value)
      this.setReviewedValue(key, value)
    },
    setReviewedValue(key, value) {
      const field = this.fieldDefinition(key)
      if (!field) return
      this.$set(this.form, field.formKey, value)
      this.markDirty()
    },
    matchingFieldIsEditing(key) {
      return this.editingMatchingFields.includes(key)
    },
    toggleMatchingFieldEdit(key) {
      if (this.matchingFieldIsEditing(key)) {
        this.editingMatchingFields = this.editingMatchingFields.filter(field => field !== key)
      } else {
        this.editingMatchingFields = [...this.editingMatchingFields, key]
      }
    },
    fieldIssue(key) {
      const aliases = this.fieldDefinition(key)?.aliases || []
      return this.issues.find(issue => {
        const text = [
          issue.category,
          issue.description,
          JSON.stringify(issue.expected_data || {}),
          JSON.stringify(issue.observed_data || {}),
        ].join(' ').toLowerCase()
        return aliases.some(alias => text.includes(alias))
      }) || null
    },
    displayValue(key, value) {
      if (this.isMissing(value)) return 'Not set'
      if (key === 'flight_domestic') return value ? 'Yes' : 'No'
      if (key === 'date') return this.formatDate(value)
      if (key === 'time') return this.normalizeTime(value)
      return value
    },
    formatDate(value) {
      const rawValue = String(value)
      let date = parseISO(rawValue)
      if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
        date = parse(rawValue, 'yyyy-MM-dd', new Date())
      }
      return isValid(date) ? format(date, 'dd MMM yyyy') : rawValue
    },
    isMissing(value) {
      return value === null || value === undefined || value === ''
    },
    normalizeTime(value) {
      return value ? String(value).slice(0, 5) : null
    },
    valuesEqual(first, second) {
      return String(first ?? '') === String(second ?? '')
    },
    comparisonValuesEqual(key, first, second) {
      const normalize = value => {
        if (this.isMissing(value)) return ''
        if (key === 'time') return this.normalizeTime(value)
        if (key === 'flight_domestic') return Boolean(value)
        if (key === 'flight_number' || key === 'airport_iata') {
          return String(value).trim().toUpperCase()
        }
        return String(value).trim()
      }
      return normalize(first) === normalize(second)
    },
    buildOverrides() {
      const segment = this.selectedSegment
      const prefix = this.direction
      const fields = [
        ['flight_number', 'flight_number'],
        [`${prefix}_date`, 'date'],
        [`${prefix}_time`, 'time'],
        [`${prefix}_airport_iata`, 'airport_iata'],
        ['airline_name', 'airline_name'],
        ['is_domestic', 'is_domestic'],
      ]
      return fields.reduce((result, [segmentKey, formKey]) => {
        let original = segment[segmentKey]
        let value = this.form[formKey]
        if (formKey === 'time') {
          original = this.normalizeTime(original)
          value = this.normalizeTime(value)
        }
        if (!this.valuesEqual(original, value)) result[segmentKey] = value
        return result
      }, {})
    },
    markDirty() {
      this.isDirty = true
      this.$emit('dirty-change', { direction: this.direction, dirty: true })
    },
    save() {
      if (this.readonly) return
      this.$emit('save', {
        direction: this.direction,
        segment_id: this.form.segment_id,
        overrides: this.buildOverrides(),
      })
    },
  },
}
</script>

<style lang="sass" scoped>
.flight-direction-editor__controls
  min-width: 0
  margin: 0
  padding: 0
  border: 0

.flight-direction-editor__controls--readonly
  pointer-events: none

.flight-direction-editor
  width: 100%
  max-width: 1120px
  margin: 0 auto
  padding: 24px 28px 30px
  background: $ui-surface

.flight-direction-editor__header
  display: flex
  align-items: flex-start
  justify-content: space-between
  gap: 20px
  margin-bottom: 18px

  h3
    margin: 5px 0 2px
    color: $ui-ink
    font-size: 20px

  p
    margin: 0
    color: $ui-muted
    font-size: 13px

.flight-direction-editor__eyebrow
  display: flex
  align-items: center
  gap: 8px

  .flight-direction-editor__eyebrow-label
    color: $ui-blue
    font-size: 12px
    font-weight: 700
    letter-spacing: .02em
    text-transform: uppercase

.flight-direction-editor__header-actions
  display: flex
  flex-shrink: 0
  align-items: center
  gap: 8px

.flight-direction-editor__preview,
.flight-direction-editor__save
  display: inline-flex
  min-height: 40px
  align-items: center
  justify-content: center
  gap: 8px
  padding: 0 14px
  border-radius: 8px
  font-size: 13px
  font-weight: 700
  cursor: pointer

  &:focus-visible
    outline: 2px solid $ui-blue
    outline-offset: 2px

.flight-direction-editor__preview
  border: 1px solid $ui-blue
  background: $ui-surface
  color: $ui-blue

  svg
    width: 18px
    height: 18px
    fill: none
    stroke: currentColor
    stroke-linecap: round
    stroke-linejoin: round
    stroke-width: 1.7

  &:hover,
  &:focus-visible
    border-color: $ui-blue
    background: $ui-blue-soft

.flight-direction-editor__save
  border: 1px solid $ui-blue
  background: $ui-blue
  color: $ui-surface
  box-shadow: 0 4px 12px rgba(23, 105, 224, .2)

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible
    background: $ui-blue-hover

  &:disabled
    border-color: $ui-line-strong
    background: $ui-line
    color: $ui-muted
    box-shadow: none
    cursor: not-allowed

.flight-direction-editor__segment
  display: grid
  grid-template-columns: minmax(360px, 720px) auto
  align-items: end
  gap: 16px
  padding: 14px 16px
  border: 1px solid $ui-line
  border-radius: 10px
  background: $ui-surface-soft

  \:deep(.form-group)
    margin: 0

.flight-direction-editor__route
  display: flex
  min-height: 38px
  align-items: center
  gap: 7px
  padding-bottom: 3px

  span,
  strong
    color: $ui-ink
    font-family: $ui-font-heading
    font-size: 15px

  small
    margin-left: 5px
    color: $ui-green
    font-size: 12px
    font-weight: 600

  .flight-direction-editor__manual
    color: $ui-amber

\:deep(.flight-comparison-summary)
  display: flex
  align-items: center
  justify-content: space-between
  gap: 16px
  margin-top: 16px
  padding: 11px 14px
  border: 1px solid $ui-line
  border-radius: 9px
  background: $ui-surface

  div
    display: flex
    align-items: center
    gap: 12px

  strong
    color: $ui-ink
    font-size: 13px

  span
    color: $ui-muted
    font-size: 12px

  .flight-comparison-summary__matching
    color: $ui-green

  .flight-comparison-summary__review
    color: $ui-amber
    font-weight: 600

  .flight-comparison-summary__review--empty
    color: $ui-muted

\:deep(.flight-comparison-section)
  margin-top: 20px

\:deep(.flight-comparison-section__header)
  display: flex
  align-items: flex-end
  justify-content: space-between
  gap: 16px
  padding-bottom: 10px
  border-bottom: 1px solid $ui-line-strong

  div > span
    display: block
    color: $ui-amber
    font-size: 10px
    font-weight: 700
    text-transform: uppercase

  strong
    display: block
    margin-top: 2px
    color: $ui-ink
    font-size: 15px

  > span
    color: $ui-muted
    font-size: 12px

\:deep(.flight-comparison-row--expanded)
  padding: 16px 0 20px
  border-bottom: 1px solid $ui-line

  > header
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    margin-bottom: 10px

    > strong
      color: $ui-ink
      font-size: 15px

\:deep(.flight-comparison-row__decision)
  display: grid
  grid-template-columns: repeat(3, minmax(0, 1fr))
  gap: 10px

\:deep(.flight-comparison-source),
\:deep(.flight-comparison-row__final)
  min-width: 0
  min-height: 86px
  padding: 11px 12px
  border: 1px solid $ui-line
  border-radius: 9px
  background: $ui-surface-soft

\:deep(.flight-comparison-source)
  display: grid
  grid-template-columns: minmax(0, 1fr) auto
  gap: 4px 10px
  color: $ui-text
  text-align: left
  cursor: pointer

  small,
  strong
    grid-column: 1

  small
    color: $ui-muted
    font-size: 10px
    font-weight: 700
    text-transform: uppercase

  strong
    overflow: hidden
    color: $ui-ink
    font-size: 14px
    text-overflow: ellipsis
    white-space: nowrap

  span
    grid-column: 2
    grid-row: 1 / span 2
    align-self: center
    color: $ui-blue
    font-size: 12px
    font-weight: 700

  &:hover,
  &:focus-visible
    border-color: $ui-blue

\:deep(.flight-comparison-source--selected)
  border-color: $ui-blue
  background: $ui-blue-soft
  box-shadow: inset 0 0 0 1px rgba(23, 105, 224, .08)

\:deep(.flight-comparison-row__final)
  background: $ui-surface

  > div:first-child
    display: flex
    align-items: center
    justify-content: space-between
    gap: 8px
    margin-bottom: 7px

    small
      color: $ui-muted
      font-size: 10px
      font-weight: 700
      text-transform: uppercase

    span
      color: $ui-green
      font-size: 10px
      font-weight: 600

\:deep(.flight-comparison-row__issue)
  margin: 9px 0 0
  padding: 8px 10px
  border-radius: 7px
  background: $ui-red-soft
  color: $ui-red
  font-size: 12px

\:deep(.flight-comparison-success)
  display: flex
  align-items: flex-start
  gap: 12px
  margin-top: 20px
  padding: 14px 16px
  border: 1px solid $ui-green
  border-radius: 9px
  background: $ui-green-soft

  > span
    display: inline-flex
    width: 24px
    height: 24px
    align-items: center
    justify-content: center
    border-radius: 50%
    background: $ui-green
    color: $ui-surface

  strong
    color: $ui-ink
    font-size: 14px

  p
    margin: 3px 0 0
    color: $ui-muted
    font-size: 12px

\:deep(.flight-matching-fields)
  margin-top: 14px

\:deep(.flight-matching-fields__toggle)
  display: flex
  width: 100%
  align-items: center
  justify-content: space-between
  gap: 12px
  padding: 11px 0
  border: 0
  border-bottom: 1px solid $ui-line
  background: transparent
  color: $ui-blue
  font-size: 13px
  font-weight: 700
  text-align: left
  cursor: pointer

  svg
    width: 18px
    height: 18px
    fill: none
    stroke: currentColor
    stroke-linecap: round
    stroke-linejoin: round
    stroke-width: 1.8
    transition: transform .15s ease

  .flight-matching-fields__chevron--open
    transform: rotate(180deg)

\:deep(.flight-matching-fields__table)
  border: 1px solid $ui-line
  border-top: 0
  border-radius: 0 0 9px 9px
  overflow: hidden

\:deep(.flight-matching-fields__columns),
\:deep(.flight-matching-row)
  display: grid
  grid-template-columns: 150px minmax(120px, 1fr) minmax(120px, 1fr) minmax(190px, 1.35fr) 66px
  align-items: center
  gap: 12px

\:deep(.flight-matching-fields__columns)
  padding: 8px 12px
  border-bottom: 1px solid $ui-line
  background: $ui-surface-soft

  span
    color: $ui-muted
    font-size: 10px
    font-weight: 700
    text-transform: uppercase

\:deep(.flight-matching-row)
  min-height: 52px
  padding: 7px 12px
  background: $ui-surface

  & + &
    border-top: 1px solid $ui-line

  > strong
    color: $ui-ink
    font-size: 13px

\:deep(.flight-matching-row__value),
\:deep(.flight-matching-row__final)
  min-width: 0

  small
    display: none

  > span
    display: block
    overflow: hidden
    color: $ui-text
    font-size: 12px
    text-overflow: ellipsis
    white-space: nowrap

\:deep(.flight-matching-row__final > span)
  color: $ui-ink
  font-weight: 600

\:deep(.flight-matching-row__action)
  display: inline-flex
  align-items: center
  justify-content: flex-end
  gap: 4px
  padding: 5px 6px
  border: 0
  background: transparent
  color: $ui-blue
  font-size: 12px
  font-weight: 700
  cursor: pointer

  span
    color: $ui-green

.flight-direction-editor__issue-summary
  margin: 12px 0 0
  padding: 9px 11px
  border-radius: 8px
  background: $ui-red-soft
  color: $ui-red
  font-size: 12px

@media (max-width: 900px)
  .flight-direction-editor
    padding: 20px

  .flight-direction-editor__segment,
  \:deep(.flight-comparison-row__decision)
    grid-template-columns: 1fr

  \:deep(.flight-matching-fields__columns)
    display: none

  \:deep(.flight-matching-row)
    grid-template-columns: 130px minmax(0, 1fr) minmax(0, 1fr) 70px

  \:deep(.flight-matching-row__final)
    grid-column: 2 / 4
    grid-row: 2

  \:deep(.flight-matching-row__value small),
  \:deep(.flight-matching-row__final small)
    display: block
    margin-bottom: 2px
    color: $ui-muted
    font-size: 9px
    font-weight: 700
    text-transform: uppercase

@media (max-width: 650px)
  .flight-direction-editor__header,
  \:deep(.flight-comparison-summary)
    align-items: stretch
    flex-direction: column

  .flight-direction-editor__header-actions
    justify-content: space-between

  \:deep(.flight-comparison-summary div)
    flex-wrap: wrap

  \:deep(.flight-matching-row)
    grid-template-columns: 1fr auto

  \:deep(.flight-matching-row__value),
  \:deep(.flight-matching-row__final)
    grid-column: 1 / -1
    grid-row: auto

  \:deep(.flight-matching-row__action)
    grid-column: 2
    grid-row: 1
</style>
